import {Component, OnDestroy, ViewChild,
	Output, EventEmitter, Input}                            from '@angular/core';
import {NgbTypeaheadSelectItemEvent}                        from "@ng-bootstrap/ng-bootstrap";

import {AngularQuery}                                       from "../../services/query/angularquery";
import {ItemComponent}                                      from "../../articles/item/item.component";
import {Source}                                             from "../../services/source/source";
import {SourceService}                                      from "../../services/source/source.service";

import {Observable}                                         from "rxjs/Observable";
import {_do}                                                from "rxjs/operator/do";
import {switchMap}                                          from "rxjs/operator/switchMap";
import {distinctUntilChanged}                               from "rxjs/operator/distinctUntilChanged";
import {debounceTime}                                       from "rxjs/operator/debounceTime";
import {_catch}                                             from "rxjs/operator/catch";
import {of}                                                 from "rxjs/observable/of";
import {isNullOrUndefined}                                  from "util";
import {Subscription}                                       from "rxjs/Subscription";


@Component({
	selector: 'source-typeahead',
	providers: [SourceService],
	template: `
        <div [class.has-danger]="searchFailed">

            <label class="font-weight-bold" for="source-typeahead-http">Source  [
                <span *ngIf="! searching">*</span>
                <span *ngIf="searching">
                    <img src="/assets/typeahead.gif" style="width: 16px; margin-top: 2px; cursor: default;" />
                </span>
                ]</label>

            <div class="input-group">

                <div class="input-group-prepend">

                    <button id="anywhere" class="btn btn-info" type="button" (click)="flip()" [hidden]="! isSearchAnywhere">
	                    
	                    Anywhere
                    
                    </button>
	                
                    <button id="startswith" class="btn btn-primary" type="button" (click)="flip()" [hidden]="isSearchAnywhere">

                        Starts with

                    </button>

                </div>

                <input #typeaheadSourceInput
                       id="source-typeahead-http"
                       type="text"
                       class="form-control"
                       [attr.tabindex]="setTabIndex"
                       [required]="setRequired"
                       [ngClass]="setSize()"
                       [(ngModel)]="model"
                       [ngbTypeahead]="sourceSearch"
                       placeholder="Search by source name"
                       [resultFormatter]="sourceFormatter"
                       [inputFormatter]="sourceFormatter"
                       (selectItem)="sourceSelectedEvent($event)"
					   (keyup.enter)="enterPressedEvent()"
                />

                <span class="input-group-btn" [hidden]="hideAddButton">
		      
			      <button class="btn btn-success"
                          type="button"
                          (click)="addSource()"
                          [disabled]="(typeaheadSourceInput.value.length < 2)"
                  >
				      Add
			      </button>
	    
		      	</span>

            </div>

            <div class="form-control-feedback" *ngIf="searchFailed">No choices found</div>

        </div>`,
})


/**
 * Provide typeahead services to a host component.
 *
 */
export class SourceTypeaheadComponent implements OnDestroy {

	// Keep track of the search 'anywhere' vs. 'starts with'
	isSearchAnywhere:boolean = true;  // If true, the value is 'Anywhere'; false = 'Starts with'

	public model:any;   // Lets the component talk to the hosting page.

	// These are semaphores used to hide or display messages.
	searchFailed = false;
	searching = false;
	sourceFound:Subscription;


	/**
	 * Initialize the component.
	 *
	 * @param {SourceService} sourceService  The service that returns Source objects.
	 */
	constructor(

		private sourceService:SourceService

	) {}


	/**
	 * Unsubscribe to avoid memory leaks.
	 */
	ngOnDestroy() {

		if (this.sourceFound) {

			this.sourceFound.unsubscribe();

		}

	}


	@Input()
	public hideAddButton:boolean = false;  // Should the 'Add' button be displayed.  This comes in from the parent page.


	@Input()
	public setRequired:boolean = false;  // Whether this is a required field or not.  This comes in from the parent page.


	@Input()
	public size:string;  // The size of the components.  This comes in from the parent page.


	@Input()
	public setTabIndex:number;  // The tab index for the host page.  This comes in from the parent page.


	@Input()
	public tag_id:number;  // The tag ID of the value from the host page.  This comes in from the parent page.


	/**
	 * Controls the Source choices INPUT.
	 */
	@ViewChild('typeaheadSourceInput')
	typeaheadSourceInput: any;


	@Output()
	public onSourceChange = new EventEmitter<Source>();  // This notifies the parent page a Source has been selected.

	@Output() 
	public onEnterPressed = new EventEmitter<any>(); // This notifies the parent page of an Enter key press

	/**
	 * Target for the 'Add' button.  This adds the selected tag to the tag list and fills in
	 * the fields on the parent page.
	 */
	addSource():void {

		// Hide the failed search.
		this.searchFailed = false;

		// Remove the dollar sign at the end, if one is found.
		if ('$' === this.typeaheadSourceInput.nativeElement.value.substr(-1, 1)) {
			let len:number = this.typeaheadSourceInput.nativeElement.value.length;
			this.typeaheadSourceInput.nativeElement.value = this.typeaheadSourceInput.nativeElement.value.substr(0, len - 1);
		}

		// Remove the uphat '^' at the beginning, if one is found.
		if ('^' === this.typeaheadSourceInput.nativeElement.value.substring(0, 1)) {
			this.typeaheadSourceInput.nativeElement.value = this.typeaheadSourceInput.nativeElement.value.substring(1);
		}

		// Create the JSON object for post after filtering the name for illegal characters.
		let json:object = {};
		json['name'] = ItemComponent.replaceWordCharacters(this.typeaheadSourceInput.nativeElement.value);

		// Create the post query.
		let query:AngularQuery = new AngularQuery();
		query.useCacheBuster(false);
		query.useParams(false);
		query.setEndpoint( AngularQuery.ENDPOINT_SOURCE, 'new' );

		// Unsubscribe from any existing query.
		if (this.sourceFound) {

			this.sourceFound.unsubscribe();

		}

		// Save the source and get back the updated data.
		this.sourceFound = this.sourceService.save(query.getUrl(), JSON.stringify(json)).subscribe(

			(response) => {

				let t:Source = new Source(response);
				this.onSourceChange.emit(t);

			},

			() => {

				window.alert('Unable to save source');

			}

		);

	}


	/**
	 * Invert the 'Anywhere'/'Starts with' display.
	 */
	flip():void {

		this.isSearchAnywhere = ! this.isSearchAnywhere;

	}

	/**
	 * Read the size variable passed in from the hosting template
	 * and set the controls based on that.  Also read the length of
	 * the INPUT field and the 'required' field and add that information
	 * to the class, too.
	 *
	 * For the size, if size is null, '' is returned.  The other choices are
	 * 'sm' and 'lg' versions of 'form-control'.
	 *
	 * For the INPUT field length, return 'ng-invalid' if the field length
	 * is zero.
	 *
	 * @returns {string}  The appropriate Bootstrap value for the form type.
	 */
	setSize(): string {

		let attribute:string = '';

		// Handle the size stuff.
		if (isNullOrUndefined(this.size)) {
			attribute = '';
		} else if (this.size.toLowerCase().startsWith('s')) {
			attribute = 'form-control-sm';
		} else if (this.size.toLowerCase().startsWith('l')) {
			attribute = 'form-control-lg';
		}

		// Handle the INPUT invalid status.
		if (! this.setRequired) {
			return attribute;
		} else if (isNullOrUndefined(this.typeaheadSourceInput.nativeElement.value) || 0 === this.typeaheadSourceInput.nativeElement.value.length) {
			return attribute + ' ng-invalid'.trim();
		} else {
			return attribute;
		}

	}


	/**
	 * Used by the typeahead to get the Source's 'name' field
	 * so it can be placed into the typeahead INPUT field when
	 * the choice is made.
	 *
	 * @param result  The Source object selected by the user.
	 */
	sourceFormatter = (result: Source) => result.name;


	/**
	 * The typeahead calls this method when the user makes
	 * a selection.  This notifies the parent page with an
	 * event that passes the data.
	 *
	 * @param selection  The event that holds the choice made by the user.
	 */
	sourceSelectedEvent(selection: NgbTypeaheadSelectItemEvent): void {

		this.onSourceChange.emit(selection.item);

	}


	/**
	 * The typeahead puts the search term into the
	 * SourceService and processes the results.  It also
	 * sets the semaphores to show/hide typeahead
	 * progress.
	 *
	 * @return The Source Observable that will return data or errors.
	 */
	sourceSearch = (tag: Observable<Source[]>) =>

		_do.call(

			switchMap.call(

				_do.call(
					distinctUntilChanged.call(
						debounceTime.call(tag, 300)),
					() => {

						this.searching = true;

					}
				),

				(search: string) =>
					_catch.call(
						_do.call(
							this.sourceService.search(search, this.isSearchAnywhere),
							(sources: Source[]) => {
								this.searchFailed = 0 === sources.length;
								this.searching = false;
							}),

						() => {

							// Report the search failure.
							this.searchFailed = true;
							this.searching = false;
							return of.call([]);

						})
			),

			() => this.searching = false

		);

	/**
	 * The typeahead calls this method whenever the Enter key is pressed
	 */
	enterPressedEvent(): void {
		this.onEnterPressed.emit();
	}
}



// WEBPACK FOOTER //
// ./src/app/typeaheads/source/source-typeahead.component.ts